import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  CContainer, 
  CRow, 
  CCol, 
  CCard, 
  CCardHeader, 
  CCardBody, 
  CButton, 
  CSpinner,
  CBadge
} from '@coreui/react';
import { Lock, Unlock } from 'lucide-react';
import api from '../../api/axiosInstance';
import swal from 'sweetalert2';

const YearWiseQuestionBank = () => {
  const { section, title } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxYear, setMaxYear] = useState(null);
  const [categoryId, setCategoryId] = useState(null); // Category ID State added

  // User Auth Details
  const authToken = localStorage.getItem('authToken');
  const userId = localStorage.getItem('ID') || localStorage.getItem('memberId');

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 9;

  const defaultThumbnail = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='350' height='200' viewBox='0 0 350 200'%3E%3Crect width='100%25' height='100%25' fill='%23f1f5f9'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='16px' fill='%2394a3b8'%3EThumbnail Image%3C/text%3E%3C/svg%3E";

  const cleanTitle = title ? title.replace(/-/g, ' ') : '';

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchYearWisePapersAndCategory();
  }, [section, title, currentPage]);

  const fetchYearWisePapersAndCategory = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Fetch Papers
      const res = await api.get(
        `/api/content/GetYearWisePapers?section=${section}&category=${encodeURIComponent(cleanTitle)}&page=${currentPage}&pageSize=${itemsPerPage}`,
        { skipAuthCheck: true }
      );

      if (res.data) {
        const fetchedPapers = res.data.papers || res.data.data || [];
        setPapers(fetchedPapers);

        if (fetchedPapers.length > 0) {
          const years = fetchedPapers.map(p => parseInt(p.year || 0)).filter(y => !isNaN(y));
          if (years.length > 0) {
            setMaxYear(Math.max(...years));
          }
        }

        const total = res.data.totalCount || res.data.total || 0;
        setTotalPages(Math.ceil(total / itemsPerPage) || 1);
      }

      // 2. Fetch Category ID dynamically
      let catId = null;
      try {
        const catRes = await api.get(`/api/categories/GetBySlug?slug=${title}`, { skipAuthCheck: true });
        if (catRes.data) {
          catId = catRes.data.id || catRes.data.Id || catRes.data.ID;
        }
      } catch (catErr) {
        console.warn("Category ID fetch by slug failed:", catErr);
      }

      if (!catId) {
        try {
          const allCatRes = await api.get('/api/Admin/GetAllCategories', { skipAuthCheck: true });
          const categories = allCatRes.data || [];
          const matchedCategory = categories.find(
            c => (c.slug && c.slug.toLowerCase() === title.toLowerCase()) || 
                 (c.title && c.title.toLowerCase().trim() === cleanTitle.toLowerCase().trim())
          );
          if (matchedCategory) {
            catId = matchedCategory.id || matchedCategory.Id;
          }
        } catch (e) {
          console.error("All categories fetch failed:", e);
        }
      }

      setCategoryId(catId);

    } catch (err) {
      console.error("Error fetching yearwise papers:", err);
      setError("Failed to load year-wise question papers.");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check access for a specific paper
  const checkUserAccess = async (paper) => {
    const isAlphaSection = String(section || '').toLowerCase().includes('alpha');
    if (isAlphaSection) return true;

    const activeTiers = JSON.parse(localStorage.getItem('activeTiers') || '[]');
    const hasSigmaTier = activeTiers.some(
      (tier) => String(tier).trim().toLowerCase() === 'sigma'
    );

    if (hasSigmaTier) return true;

    try {
      const subRes = await api.get(`/api/members/${userId}/subscription`);
      const subData = subRes.data?.data || subRes.data;
      const userSubs = Array.isArray(subData) ? subData : subData ? [subData] : [];

      const isSigmaInApi = userSubs.some((sub) => {
        const tierName = String(sub?.tierName || sub?.tier || sub?.sectionName || '').toLowerCase();
        return tierName.includes('sigma');
      });

      if (isSigmaInApi) return true;

      const allowedCategoryIds = userSubs.flatMap((sub) => {
        const ids = sub.categoryIds || sub.CategoryIds || '';
        return String(ids).split(',').map((catId) => catId.trim());
      });

      const paperCatId = paper.categoryId || paper.CategoryId || paper.category_id;
      const paperCatIds = Array.isArray(paperCatId) 
        ? paperCatId.map((catId) => String(catId).trim()) 
        : paperCatId !== undefined && paperCatId !== null 
          ? String(paperCatId).split(',').map((catId) => catId.trim()) 
          : [];

      const isSubscribed = paperCatIds.length > 0 
        ? paperCatIds.some((catId) => allowedCategoryIds.includes(catId))
        : userSubs.some((sub) => {
            const catName = String(sub?.categoryName || sub?.category || '').toLowerCase();
            return catName && cleanTitle.toLowerCase().includes(catName);
          });

      return isSubscribed;
    } catch (err) {
      console.error("Subscription verification failed:", err);
      return false;
    }
  };

  const handleGetPaper = async (paper) => {
    const paperYear = parseInt(paper.year || 0);
    const isLatest = paperYear === maxYear;

    if (isLatest) {
      if (!authToken) {
        navigate('/login', { state: { from: `/${section}/${title}/year-wise/${paper.year || paper.id}` } });
        return;
      }
      navigate(`/${section}/${title}/year-wise/${paper.year || paper.id}`);
      return;
    }

    if (!authToken) {
      navigate('/login', { state: { from: `/${section}/${title}/year-wise/${paper.year || paper.id}` } });
      return;
    }

    const hasAccess = await checkUserAccess(paper);

    if (!hasAccess) {
      swal.fire({
        title: "Subscription Required",
        text: "To view this paper, please subscribe to this category or Sigma Plan.",
        icon: "warning",
        confirmButtonText: "Subscribe Now",
        allowOutsideClick: false
      }).then((result) => {
        if (result.isConfirmed) {
          navigate(`/plans?categoryId=${categoryId || ''}&categoryName=${encodeURIComponent(cleanTitle)}`);
        }
      });
      return;
    }

    navigate(`/${section}/${title}/year-wise/${paper.year || paper.id}`);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5 py-5">
        <CSpinner color="primary" size="lg" />
        <p className="text-muted mt-2">Loading Previous Year Questions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <CContainer className="text-center mt-5 py-4">
        <h5 className="text-danger mb-3">{error}</h5>
        <CButton color="primary" size="sm" onClick={() => navigate(-1)}>Go Back</CButton>
      </CContainer>
    );
  }

  return (
    <>
      <div 
        className="w-100 text-white text-center py-5 shadow-sm mb-4" 
        style={{ 
          backgroundColor: 'rgb(0, 33, 71)',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)',
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          transform: 'translateX(-50%)'
        }}
      >
        <div className="container py-2 px-3">
          <h1 className="fw-bold mb-2 display-6" style={{ color: 'yellow' }}>
            {cleanTitle.toUpperCase()} Previous Year Questions
          </h1>
          <p className="lead m-0 opacity-90 text-light fs-6">
            {section.toUpperCase()} • Year-wise Question Papers & Solutions
          </p>
        </div>
      </div>

      <CContainer className="pb-5">
        {papers.length > 0 ? (
          <CRow className="g-4">
            {papers.map((paper) => {
              const paperYear = parseInt(paper.year || 0);
              const isLatest = paperYear === maxYear;
              const isLocked = isLatest ? !authToken : false;

              return (
                <CCol lg={4} md={6} sm={12} key={paper.id || paper.year}>
                  <CCard className="h-100 border-2 border-dark shadow-sm rounded-3 overflow-hidden custom-card-hover">
                    
                    <CCardHeader className="bg-white border-bottom border-2 border-dark text-center py-2">
                      <h5 className="fw-bold m-0 text-dark">
                        {paper.title || `${cleanTitle.toUpperCase()} ${paper.year}`}
                      </h5>
                    </CCardHeader>

                    <CCardBody className="p-0 bg-light d-flex align-items-center justify-content-center" style={{ height: '180px' }}>
                      <img 
                        src={paper.thumbnail || defaultThumbnail} 
                        alt={paper.title || 'Paper Thumbnail'} 
                        className="w-100 h-100 object-fit-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = defaultThumbnail;
                        }}
                      />
                    </CCardBody>

                    <div className="p-0">
                      <CButton 
                        color={isLocked ? "secondary" : "danger"}
                        className="w-100 fw-bold text-white rounded-0 py-2 d-flex align-items-center justify-content-center gap-2 border-top border-2 border-dark"
                        style={{ backgroundColor: isLocked ? '#6c757d' : '#c1121f' }}
                        onClick={() => handleGetPaper(paper)}
                      >
                        <span>
                          {isLatest 
                            ? (isLocked ? "Get Paper (Login Required)" : "Get Paper") 
                            : "Get Paper"
                          }
                        </span>
                        {isLocked ? <Lock size={16} /> : <Unlock size={16} />}
                      </CButton>
                    </div>

                  </CCard>
                </CCol>
              );
            })}
          </CRow>
        ) : (
          <div className="alert alert-warning text-center">No question papers available for this category.</div>
        )}

        {totalPages > 0 && (
          <div className="mt-5 p-3 bg-white border border-2 border-dark rounded-3 d-flex align-items-center justify-content-center gap-3 flex-wrap">
            <span className="fw-bold text-dark">Page No:</span>
            
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`btn btn-sm fw-bold border ${currentPage === page ? 'btn-dark text-white' : 'btn-light text-dark'}`}
                  style={{ minWidth: '35px' }}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        )}

        <style>{`
          .custom-card-hover {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }
          .custom-card-hover:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 16px rgba(0,0,0,0.2) !important;
          }
        `}</style>
      </CContainer>
    </>
  );
};

export default YearWiseQuestionBank;